/**
 * `/settings/firm` — **Your firm** (C11, FR-A8 §4–§5; T-1.17).
 *
 * Five sections — Identity, Registration, Address, Contact, Working hours —
 * and **one Save for the page**, on the edit bar (Wave 14E, T-4.67; ADR-0032
 * §5). Until then each section had its own Save. The one Save still **sends
 * only the fields that changed** (`profileChanges`), so a firm fixing its
 * landline does not re-send, and so cannot be refused for, a GSTIN it did not
 * touch; and a section's rule is checked only when that section changed
 * (`pageProblem`). The side nav marks each section with unsaved changes.
 *
 * The GSTIN rules are checked here before the request — shape, the state's
 * GST code in its first two digits, the PAN at 3–12 — so a mismatch is shown
 * beside the field and never sent (T-1.17's test). The service holds the same
 * rules for when this screen is not the one writing.
 *
 * **The logo saves itself** (w13a, ADR-0030 §2): it is not a section field,
 * never makes the page dirty and never rides the bar's Save.
 *
 * **Social profiles & map** was a sixth section here from 2026-09-25 until
 * Wave 14 moved it to its own page under the website (C13, ADR-0032 §1:
 * `routes/Site/Social.tsx`, `/site/social`); `/settings/firm#social` redirects
 * there. A link to any other section's hash is scrolled to once the page has
 * loaded (the sections do not exist before that).
 *
 * **Import from organisation** (ADR-0029): shown only when the platform's
 * organisation holds something that fills a blank or differs. The dialog lists
 * each field with both values and a tick, nothing ticked to begin with, and
 * takes only what is ticked. Nothing is written back to core.
 */

import { Alert, Badge, Button, Checkbox, Dialog, Input, Select, Skeleton, Textarea } from '@fiveox/ui'
import { Building2, Clock, Download, FileText, MapPin, Phone } from 'lucide-react'
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router'

import { EditBar, useLeaveGuard } from '../../../components/edit'
import { LogoControl } from '../../../components/LogoControl'
import { SETTINGS, SITE, useEvaluation } from '../../../lib/access'
import { ApiError } from '../../../lib/api'
import { type FirmProfile as Profile, type OrgSuggestion, firm } from '../../../lib/api/firm'
import { STATES, changedFields, gstinProblem, hoursProblem, shapeProblem } from './rules'
import { SettingsPage, SettingsSection } from './SettingsPage'
import { WorkingHoursEditor } from './WorkingHoursEditor'

type Field = keyof Profile

const SECTIONS: Array<{ id: string; title: string; fields: Field[] }> = [
  // `logo_url` is not a section field: the logo control saves itself (w13a,
  // ADR-0030 §2), so it never makes Identity dirty or rides its Save.
  { id: 'identity', title: 'Identity', fields: ['legal_name', 'display_name', 'about'] },
  { id: 'registration', title: 'Registration', fields: ['pan', 'gstin', 'tan'] },
  {
    id: 'address',
    title: 'Address',
    fields: ['address_line1', 'address_line2', 'city', 'state', 'pincode'],
  },
  { id: 'contact', title: 'Contact', fields: ['email', 'mobile', 'landline'] },
  { id: 'hours', title: 'Working hours', fields: ['working_hours'] },
]

function pick(source: Partial<Profile>, fields: Field[]): Partial<Profile> {
  const out: Record<string, unknown> = {}
  for (const field of fields) out[field] = source[field] ?? null
  return out as Partial<Profile>
}

/** What each section refuses before the request, or `null`. */
export function sectionProblem(
  id: string,
  draft: Partial<Profile>,
  original: Partial<Profile>,
): string | null {
  if (id === 'registration') {
    return (
      shapeProblem('pan', draft.pan) ??
      shapeProblem('gstin', draft.gstin) ??
      shapeProblem('tan', draft.tan) ??
      gstinProblem({ gstin: draft.gstin, state: draft.state, pan: draft.pan })
    )
  }
  if (id === 'address' && (draft.state ?? null) !== (original.state ?? null)) {
    // The state is checked against the GSTIN **as saved**, which is what the
    // service will compare it with.
    return gstinProblem({ gstin: original.gstin, state: draft.state, pan: original.pan })
  }
  if (id === 'hours') return hoursProblem(draft.working_hours ?? null)
  return null
}

/** Every field a section owns — what the one Save may send. */
const PAGE_FIELDS: Field[] = SECTIONS.flatMap((section) => section.fields)

/** The page's PATCH body: only the fields that changed, across every section. */
export function profileChanges(
  original: Partial<Profile>,
  draft: Partial<Profile>,
): Partial<Profile> {
  return changedFields(original, draft, PAGE_FIELDS)
}

/** Why the one Save is refused, or `null` — the first changed section's rule
 *  that fails, named, so the bar says where to look. A section nobody touched
 *  is not checked: a stored value it would refuse is not this Save's to fix. */
export function pageProblem(
  original: Partial<Profile>,
  draft: Partial<Profile>,
): string | null {
  for (const section of SECTIONS) {
    if (Object.keys(changedFields(original, draft, section.fields)).length === 0) continue
    const problem = sectionProblem(section.id, draft, original)
    if (problem) return `${section.title}: ${problem}`
  }
  return null
}

function ImportDialog({
  open,
  onClose,
  suggestions,
  onImport,
}: {
  open: boolean
  onClose: () => void
  suggestions: OrgSuggestion[]
  onImport: (changes: Partial<Profile>) => Promise<void>
}) {
  const [ticked, setTicked] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setTicked(new Set())
      setError(null)
    }
  }, [open])

  const toggle = (field: string, on: boolean) =>
    setTicked((prev) => {
      const next = new Set(prev)
      if (on) next.add(field)
      else next.delete(field)
      return next
    })

  const take = async () => {
    const changes: Record<string, unknown> = {}
    for (const row of suggestions) if (ticked.has(row.field)) changes[row.field] = row.suggested
    setBusy(true)
    setError(null)
    try {
      await onImport(changes as Partial<Profile>)
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Those fields could not be imported.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Import from organisation" width="lg" error={error}>
      <p className="text-muted-foreground text-sm">
        Your organisation’s profile on the platform has these details. Tick the ones to copy into
        ProDesk — nothing else changes, and nothing is sent back to the platform.
      </p>
      <div className="mt-3 flex justify-end">
        <Button
          variant="link"
          size="sm"
          onClick={() =>
            setTicked(
              ticked.size === suggestions.length
                ? new Set()
                : new Set(suggestions.map((row) => row.field)),
            )
          }
        >
          {ticked.size === suggestions.length ? 'Clear all' : 'Tick all'}
        </Button>
      </div>
      <ul className="divide-border border-border mt-1 max-h-[55vh] divide-y overflow-y-auto rounded-xl border">
        {suggestions.map((row) => (
          <li key={row.field} data-suggestion={row.field} className="px-4 py-3">
            <Checkbox
              checked={ticked.has(row.field)}
              onChange={(e) => toggle(row.field, e.target.checked)}
              label={
                <span className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-3">
                  <span className="text-foreground w-32 shrink-0 font-medium">{row.label}</span>
                  <span className="min-w-0 text-sm">
                    <span className="text-foreground font-medium break-words">{row.suggested}</span>
                    <span className="text-muted-foreground block text-xs">
                      {row.fills_blank ? 'Blank in ProDesk' : `ProDesk has: ${row.current}`}
                    </span>
                  </span>
                </span>
              }
            />
          </li>
        ))}
      </ul>
      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={() => void take()} disabled={ticked.size === 0} loading={busy}>
          {ticked.size === 0
            ? 'Tick what to import'
            : `Import ${ticked.size} field${ticked.size === 1 ? '' : 's'}`}
        </Button>
      </div>
    </Dialog>
  )
}

export function FirmProfilePage() {
  const { can } = useEvaluation()
  const editable = can(SETTINGS, 'can_update')
  const [original, setOriginal] = useState<Partial<Profile> | null>(null)
  const [draft, setDraft] = useState<Partial<Profile>>({})
  const [suggestions, setSuggestions] = useState<OrgSuggestion[]>([])
  const [importOpen, setImportOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const loadSuggestions = useCallback(async () => {
    try {
      setSuggestions((await firm.orgSuggestions()).data.fields)
    } catch {
      // ADR-0029 §3 — no suggestion, never an error on a settings page.
      setSuggestions([])
    }
  }, [])

  useEffect(() => {
    firm
      .read()
      .then((body) => {
        setOriginal(body.data.profile)
        setDraft(body.data.profile)
      })
      .catch(() => setError('Your firm’s details could not be loaded.'))
    void loadSuggestions()
  }, [loadSuggestions])

  const dirtyIds = useMemo(
    () =>
      original
        ? SECTIONS.filter(
            (section) => Object.keys(changedFields(original, draft, section.fields)).length > 0,
          ).map((section) => section.id)
        : [],
    [original, draft],
  )
  const dirty = dirtyIds.length > 0
  const problem = original && dirty ? pageProblem(original, draft) : null
  const guard = useLeaveGuard(dirty)

  // A link into a section lands after the skeleton, so the browser's own jump
  // to the fragment has already missed. (`#social` is not one any more — the
  // router sends it to `/site/social` before this page mounts.)
  const { hash } = useLocation()
  useEffect(() => {
    if (!original || !hash) return
    document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView?.({ block: 'start' })
  }, [original, hash])

  const set = (field: Field, value: unknown) => {
    setDraft((prev) => ({ ...prev, [field]: value }))
  }

  const applySaved = (saved: Profile, fields: Field[]) => {
    setOriginal(saved)
    // Keep every *other* section's unsaved edits; take the saved ones back.
    setDraft((prev) => ({ ...prev, ...pick(saved, fields) }))
  }

  const save = async () => {
    if (!original) return
    const changes = profileChanges(original, draft)
    if (problem || Object.keys(changes).length === 0) return
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      const body = await firm.updateProfile(changes)
      applySaved(body.data, Object.keys(changes) as Field[])
      void loadSuggestions()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Your changes could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  // w13a — ADR-0030. The one logo, written at once; every other section's
  // unsaved edits are kept, as `applySaved` keeps them.
  const saveLogo = async (next: string | null) => {
    const body = await firm.updateProfile({ logo_url: next })
    applySaved(body.data, ['logo_url'])
  }

  const importFields = async (changes: Partial<Profile>) => {
    const body = await firm.updateProfile(changes)
    const fields = Object.keys(changes) as Field[]
    applySaved(body.data, fields)
    setNotice(
      `Imported ${fields.length} field${fields.length === 1 ? '' : 's'} from your organisation.`,
    )
    await loadSuggestions()
  }

  const text = (field: Field, label: string, extra: Record<string, unknown> = {}) => (
    <Input
      label={label}
      value={(draft[field] as string | null) ?? ''}
      disabled={!editable}
      onChange={(e) => set(field, e.target.value)}
      {...extra}
    />
  )

  const icons: Record<string, ReactNode> = {
    identity: <Building2 size={18} aria-hidden />,
    registration: <FileText size={18} aria-hidden />,
    address: <MapPin size={18} aria-hidden />,
    contact: <Phone size={18} aria-hidden />,
    hours: <Clock size={18} aria-hidden />,
  }

  return (
    <SettingsPage
      title="Your firm"
      description="What your invoices, website and messages say about the practice."
      actions={
        editable && suggestions.length > 0 ? (
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Download size={16} aria-hidden /> Import from organisation
            <Badge tone="info" size="sm">
              {suggestions.length}
            </Badge>
          </Button>
        ) : undefined
      }
    >
      {guard}
      {error && <Alert tone="error">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      {!original ? (
        <div className="space-y-4" aria-busy="true">
          {SECTIONS.map((s) => (
            <Skeleton key={s.id} className="h-40 w-full rounded-2xl" />
          ))}
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[180px_minmax(0,1fr)]">
          <nav aria-label="Sections" className="hidden lg:block">
            <ul className="sticky top-6 space-y-1 text-sm">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-muted-foreground hover:bg-muted hover:text-foreground flex items-center justify-between rounded-lg px-3 py-2"
                  >
                    {s.title}
                    {dirtyIds.includes(s.id) && (
                      <span className="bg-primary h-2 w-2 rounded-full" aria-label="unsaved" />
                    )}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="space-y-6">
            <SettingsSection
              id="identity"
              icon={icons.identity}
              title="Identity"
              description="The names you trade under, your logo, and a line about the practice."
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="border-border sm:col-span-2 border-b pb-4">
                  <LogoControl
                    value={original.logo_url ?? null}
                    profession={original.profession ?? null}
                    editable={editable}
                    canBrowse={can(SITE, 'can_view')}
                    canUpload={can(SITE, 'can_update')}
                    onChange={saveLogo}
                  />
                </div>
                {text('legal_name', 'Legal name', { hint: 'As registered — printed on invoices.' })}
                {text('display_name', 'Display name', { hint: 'What clients call you, if different.' })}
                <div className="sm:col-span-2">
                  <Textarea
                    label="About the practice"
                    rows={3}
                    maxLength={2000}
                    value={draft.about ?? ''}
                    disabled={!editable}
                    onChange={(e) => set('about', e.target.value)}
                  />
                </div>
              </div>
            </SettingsSection>

            <SettingsSection
              id="registration"
              icon={icons.registration}
              title="Registration"
              description="Each is optional. A GSTIN must agree with your state and your PAN."
            >
              <div className="grid gap-4 sm:grid-cols-3">
                {(['pan', 'gstin', 'tan'] as const).map((field) => (
                  <Input
                    key={field}
                    label={field.toUpperCase()}
                    value={(draft[field] as string | null) ?? ''}
                    disabled={!editable}
                    maxLength={field === 'gstin' ? 15 : 10}
                    autoCapitalize="characters"
                    spellCheck={false}
                    className="font-mono tracking-wide"
                    error={
                      shapeProblem(field, draft[field]) ??
                      (field === 'gstin' && dirtyIds.includes('registration')
                        ? gstinProblem({ gstin: draft.gstin, state: draft.state, pan: draft.pan })
                        : null)
                    }
                    onChange={(e) => set(field, e.target.value.toUpperCase())}
                  />
                ))}
              </div>
            </SettingsSection>

            <SettingsSection
              id="address"
              icon={icons.address}
              title="Address"
              description="Your registered office. The state decides your place of supply."
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">{text('address_line1', 'Address line 1')}</div>
                <div className="sm:col-span-2">{text('address_line2', 'Address line 2')}</div>
                {text('city', 'City')}
                <Select
                  label="State or union territory"
                  value={draft.state ?? ''}
                  disabled={!editable}
                  onChange={(e) => set('state', e.target.value || null)}
                >
                  <option value="">Choose a state</option>
                  {STATES.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </Select>
                {text('pincode', 'PIN code', { inputMode: 'numeric', maxLength: 10 })}
                <Input
                  label="Country"
                  value={draft.country ?? 'India'}
                  disabled
                  hint="ProDesk serves practices in India."
                />
              </div>
            </SettingsSection>

            <SettingsSection
              id="contact"
              icon={icons.contact}
              title="Contact"
              description="Where clients and your website’s visitors reach the office."
            >
              <div className="grid gap-4 sm:grid-cols-3">
                {text('email', 'Email', { type: 'email', inputMode: 'email' })}
                {text('mobile', 'Mobile', { type: 'tel', inputMode: 'tel' })}
                {text('landline', 'Landline', { type: 'tel', inputMode: 'tel', maxLength: 20 })}
              </div>
            </SettingsSection>

            <SettingsSection
              id="hours"
              icon={icons.hours}
              title="Working hours"
              description="When the office is open. Your website’s footer can show them."
            >
              <WorkingHoursEditor
                value={draft.working_hours ?? null}
                disabled={!editable}
                onChange={(next) => set('working_hours', next)}
              />
            </SettingsSection>

          </div>
        </div>
      )}

      {original && (
        <EditBar
          dirty={dirty}
          saving={saving}
          problem={problem}
          editable={editable}
          onSave={() => void save()}
          onDiscard={() => setDraft(original)}
        />
      )}

      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        suggestions={suggestions}
        onImport={importFields}
      />
    </SettingsPage>
  )
}
